/* eslint-disable import/order */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type QueryConstraint,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { SubKreweApprovalTask } from '~/shared/models/tasks/SubKreweApprovalTask.js'
import type { SubKreweJoinRequestTask } from '~/shared/models/tasks/SubKreweJoinRequestTask.js'
import type { WithNotifications } from '~/shared/models/traits/WithNotifications.js'
import { TaskFactory } from '../shared/models/TaskFactory.js'
import type { Task, TaskFirestoreData, TaskStatus, TaskType } from '../shared/types/task.js'
import { hasAssignment, hasDeadline, hasNotifications, hasReferences } from '../shared/utils/traits.js'
import { useMembersStore } from './members.js'
import { useSubKrewesStore } from './subkrewes.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, holdLive, isLiveReady, liveDocListener, liveQueryListener } from '~/utils/liveData'

// The open statuses, for subscriptions that leave out completed tasks
const OPEN_STATUSES: TaskStatus[] = ['pending', 'in_progress']

type TaskNotice = { taskId: string, type: 'created' | 'updated' | 'completed' }

/**
 * Store for managing tasks data
 *
 * Tasks are created and managed through the task system, with various implementations
 * for different types of tasks. This store handles the persistence and state management
 * for all task types.
 */
export const useTasksStore = defineStore('tasks', {
  state: () => ({
    // Store tasks by document ID for O(1) lookups
    tasks: {} as Record<string, Task>,
    loading: false,
    error: null as string | null,
    activeSubscriptions: [] as (() => void)[],
    initialized: false,
  }),

  getters: {
    /**
     * Convert map to array for components that expect arrays
     */
    tasksArray(): Task[] {
      return Object.values(this.tasks)
    },

    /**
     * Get pending tasks
     */
    pendingTasks(): Task[] {
      return this.tasksArray.filter(task => task.status === 'pending')
    },

    /**
     * Get tasks assigned to a specific entity
     */
    tasksForEntity(): (entityId: string, entityType: 'member' | 'subkrewe') => Task[] {
      return (entityId: string, entityType: 'member' | 'subkrewe') => {
        return this.tasksArray.filter((task) => {
          // Check metadata for assignment information
          if (task.metadata) {
            return task.metadata.assignedToId === entityId
              && task.metadata.assignedToType === entityType
          }
          return false
        })
      }
    },

    /**
     * Get tasks by type
     */
    tasksByType(): (taskType: TaskType) => Task[] {
      return (taskType: TaskType) => {
        return this.tasksArray.filter(task => task.taskType === taskType)
      }
    },

    /**
     * Get task by ID
     */
    getTaskById(): (id: string) => Task | undefined {
      return (id: string) => {
        const path = `tasks/${id}`
        return this.tasks[path]
      }
    },
  },

  actions: {
    /**
     * Create a new task
     * @param task Task to create
     * @returns ID of the created task
     */
    async createTask(task: Task): Promise<string> {
      this.loading = true
      this.error = null

      try {
        // Convert to Firestore data
        const taskData = TaskFactory.createFirestoreData(task)

        // Save to Firestore
        const db = getFirestore()
        const docRef = await addDoc(collection(db, 'tasks'), taskData as { [x: string]: never })

        // Update the task with the ID
        task.id = docRef.id

        // Add to local state using $patch
        this.$patch((state) => {
          state.tasks[docRef.path] = task
        })

        // When the task is created successfully, check if we should notify
        if (task.id) {
          // Only send notification if not explicitly suppressed
          const shouldNotify = !task.metadata?.suppressCreationNotification

          if (shouldNotify && hasNotifications(task)) {
            // Existing notification code...
          }
        }

        return docRef.id
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error creating task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * A task, live while the caller holds it (utils/liveData.ts). Throws
     * when it can't be read.
     */
    async fetchTask(id: string): Promise<Task | null> {
      this.loading = true
      this.error = null
      try {
        await acquireLive(this.taskSpec(id))
        return this.tasks[`tasks/${id}`] ?? null
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error fetching task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Update a task
     * @param task Task to update
     */
    async updateTask(task: Task): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        if (!task.id) {
          throw new Error('Task has no ID')
        }

        // Update timestamp
        task.updatedAt = new Date()

        // Convert to Firestore data
        const taskData = TaskFactory.createFirestoreData(task)

        // Update in Firestore - using a more precise type assertion
        const db = getFirestore()
        await updateDoc(doc(db, 'tasks', task.id), taskData as { [x: string]: never })

        // Update in local state
        this.$patch((state) => {
          const path = `tasks/${task.id}`
          state.tasks[path] = task
        })

        return true
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error updating task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
     * Update a task's status
     * @param id Task ID
     * @param status New status
     */
    async updateTaskStatus(id: string, status: TaskStatus): Promise<boolean> {
      const task = this.getTaskById(id)
      if (!task) {
        throw new Error(`Task not found: ${id}`)
      }

      // Store the old status for notification
      const oldStatus = task.status

      // Update the status
      task.status = status
      task.updatedAt = new Date()

      // Set completedAt for completed tasks
      if (status === 'completed') {
        // Set completedAt either through the trait or directly if property exists
        if (hasDeadline(task)) {
          task.completedAt = new Date()
        }
        else if ('completedAt' in task) {
          (task as Task & { completedAt: Date }).completedAt = new Date()
        }
      }

      // Send notifications if task supports them
      if (hasNotifications(task)) {
        // Use explicit casting after type guard verification
        const notifiableTask = task as Task & WithNotifications

        // Determine who should receive the notification
        const recipients: string[] = []

        // If the task has an assigned member, notify them
        if (hasAssignment(task) && task.assignedToType === 'member' && typeof task.assignedToId === 'string') {
          recipients.push(task.assignedToId)
        }

        // If the task has references (like creator, watchers), notify them too
        if (hasReferences(task)) {
          // Use explicit casting for reference methods
          const referencesTask = task as Task & {
            getReferencesByType: (type: string) => EntityReference[]
          }

          // Get creator reference if it exists
          const creatorRefs = referencesTask.getReferencesByType('creator')
          for (const ref of creatorRefs) {
            if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
              recipients.push(ref.entityId)
            }
          }

          // Get watcher references if they exist
          const watcherRefs = referencesTask.getReferencesByType('watcher')
          for (const ref of watcherRefs) {
            if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
              recipients.push(ref.entityId)
            }
          }
        }

        // Send notifications to all recipients
        for (const recipientId of recipients) {
          await notifiableTask.notifyStatusChanged(recipientId, oldStatus, status)
        }
      }

      // Save the changes
      return await this.updateTask(task)
    },

    /**
     * Delete a task
     * @param id Task ID to delete
     */
    async deleteTask(id: string): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        const db = getFirestore()
        // Delete from Firestore
        await deleteDoc(doc(db, 'tasks', id))

        // Remove from local state
        this.$patch((state) => {
          const path = `tasks/${id}`

          delete state.tasks[path]
        })

        return true
      }
      catch (error) {
        this.error = (error as Error).message
        console.error('Error deleting task:', error)
        throw error
      }
      finally {
        this.loading = false
      }
    },

    /**
    * Execute a specific action on a task
    * @param taskId ID of the task to execute action on
    * @param action Name of the action method to call
    * @param actionArgs Additional arguments to pass to the action method
    * @returns True if action was successful
    */
    async executeTaskAction(taskId: string, action: string, ...actionArgs: unknown[]): Promise<boolean> {
      this.loading = true
      this.error = null

      try {
        // Get the task
        const task = await this.fetchTask(taskId)
        if (!task) {
          throw new Error(`Task not found: ${taskId}`)
        }

        // Check if the task has the requested action method
        if (typeof task[action] !== 'function') {
          throw new Error(`Task does not support action: ${action}`)
        }

        // Store old status for notification
        const oldStatus = task.status

        // Call the action method using function indexing AND pass through the arguments
        const actionFn = task[action] as (...args: unknown[]) => Promise<boolean>
        const result = await actionFn.call(task, ...actionArgs)

        if (result) {
          // If the action was successful and changed the status, send notification
          if (hasNotifications(task) && task.status !== oldStatus) {
            // Use explicit casting after type guard verification
            const notifiableTask = task as Task & WithNotifications

            // Determine who should receive notifications
            const recipients: string[] = []

            // If the task has an assigned member, notify them
            if (hasAssignment(task) && task.assignedToType === 'member' && typeof task.assignedToId === 'string') {
              recipients.push(task.assignedToId)
            }

            // If the task has references (like creator, watchers), notify them too
            if (hasReferences(task)) {
              // Use explicit casting for reference methods
              const referencesTask = task as Task & {
                getReferencesByType: (type: string) => EntityReference[]
              }

              // Get creator reference if it exists
              const creatorRefs = referencesTask.getReferencesByType('creator')
              for (const ref of creatorRefs) {
                if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
                  recipients.push(ref.entityId)
                }
              }

              // Get watcher references if they exist
              const watcherRefs = referencesTask.getReferencesByType('watcher')
              for (const ref of watcherRefs) {
                if (ref.entityType === 'member' && typeof ref.entityId === 'string' && !recipients.includes(ref.entityId)) {
                  recipients.push(ref.entityId)
                }
              }
            }

            // Send notifications to all recipients
            for (const recipientId of recipients) {
              await notifiableTask.notifyStatusChanged(recipientId, oldStatus, task.status)
            }
          }

          // Update the task in Firestore
          await this.updateTask(task)
        }

        return result
      }
      catch (error) {
        this.error = error instanceof Error ? error.message : 'Unknown error'
        console.error(`Error executing task action ${action}:`, error)
        return false
      }
      finally {
        this.loading = false
      }
    },

    /**
    * Hold the tasks assigned to an entity live
    * @param entityId Entity ID
    * @param entityType Entity type
    * @param options Additional subscription options
    * @returns The release
    */
    subscribeToEntityTasks(
      entityId: string,
      entityType: 'member' | 'subkrewe',
      options: {
        roleFiltering?: boolean // Whether to apply role-based filtering
        includeCompleted?: boolean // Whether to include completed tasks
        notifyOnChanges?: boolean // Whether to create notifications for task changes
        category?: string // For subscription tracking
      } = {},
    ): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) return null

      const constraints: QueryConstraint[] = [
        where('assignedToId', '==', entityId),
        where('assignedToType', '==', entityType),
      ]
      if (options.includeCompleted !== true) constraints.push(where('status', 'in', OPEN_STATUSES))
      return holdLive(this.tasksQuerySpec(
        `assignedToType=${entityType}&assignedToId=${entityId}${options.includeCompleted === true ? '' : '&open'}`,
        constraints,
        options.notifyOnChanges === true,
      )).release
    },

    /**
    * Subscribe to all tasks relevant to the current user based on their role
    * @param options Subscription options
    * @returns Array of unsubscribe functions
    */
    subscribeToUserRelevantTasks(options: {
      includeCompleted?: boolean
      notifyOnChanges?: boolean
    } = {}): Array<() => void> {
      const user = useCurrentUser().value
      if (!user?.uid) return []

      const membersStore = useMembersStore()
      const memberPath = `members/${user.uid}`
      const currentMember = membersStore.members[memberPath]

      if (!currentMember) {
        console.warn('[Tasks] Cannot setup relevant task subscriptions: Current member not found')
        return []
      }

      const unsubscribers: Array<(() => void) | null> = []

      // Subscribe to tasks assigned directly to the user
      const personalTasksUnsubscribe = this.subscribeToEntityTasks(
        user.uid,
        'member',
        {
          roleFiltering: false, // No need to filter user's own tasks
          includeCompleted: options.includeCompleted,
          notifyOnChanges: options.notifyOnChanges,
          category: 'tasks-personal',
        },
      )
      if (personalTasksUnsubscribe) unsubscribers.push(personalTasksUnsubscribe)

      // CRITICAL: For all user types, also subscribe to tasks where they are in metadata
      // This ensures join requests show up for the member
      const metadataTasksUnsubscribe = this.subscribeToTasksWithMetadata(
        'memberId',
        user.uid,
        {
          includeCompleted: options.includeCompleted,
          category: 'tasks-metadata',
        },
      )
      if (metadataTasksUnsubscribe) unsubscribers.push(metadataTasksUnsubscribe)

      // Other role-specific subscriptions...

      return unsubscribers.filter(Boolean) as Array<() => void>
    },

    subscribeToTasksWithMetadata(
      metadataField: string,
      metadataValue: string,
      options: {
        includeCompleted?: boolean
        category?: string
      } = {},
    ): (() => void) | null {
      const constraints: QueryConstraint[] = [where(`metadata.${metadataField}`, '==', metadataValue)]
      if (!options.includeCompleted) constraints.push(where('status', 'in', OPEN_STATUSES))
      return holdLive(this.tasksQuerySpec(
        `metadata.${metadataField}=${metadataValue}${options.includeCompleted ? '' : '&open'}`,
        constraints,
        false,
      )).release
    },

    /**
    * Hold the tasks of a type live
    * @param taskType Task type to subscribe to
    * @param options Additional subscription options
    * @returns The release
    */
    subscribeToTaskType(
      taskType: TaskType,
      options: {
        status?: TaskStatus | TaskStatus[]
        includeCompleted?: boolean
        notifyOnChanges?: boolean
        category?: string
      } = {},
    ): (() => void) | null {
      const user = useCurrentUser().value
      if (!user?.uid) return null

      const constraints: QueryConstraint[] = [where('taskType', '==', taskType)]
      let statusKey = ''
      if (options.status) {
        const statuses = Array.isArray(options.status) ? options.status : [options.status]
        constraints.push(Array.isArray(options.status) ? where('status', 'in', options.status) : where('status', '==', options.status))
        statusKey = `&status=${statuses.join(',')}`
      }
      else if (options.includeCompleted !== true) {
        constraints.push(where('status', 'in', OPEN_STATUSES))
        statusKey = '&open'
      }
      return holdLive(this.tasksQuerySpec(`taskType=${taskType}${statusKey}`, constraints, options.notifyOnChanges === true)).release
    },

    /**
     * A live query on tasks, newest first. With `notify`, tasks that
     * arrive or change status after the first snapshot are announced
     * (createTaskNotifications); the first snapshot never is.
     */
    tasksQuerySpec(filterKey: string, constraints: QueryConstraint[], notify: boolean): LiveSpec<TaskFirestoreData> {
      const key = `tasks?${filterKey}${notify ? '&notify' : ''}`
      return {
        key,
        listen: (emit, fail) => onSnapshot(
          query(collection(useFirestore(), 'tasks'), ...constraints, orderBy('createdAt', 'desc')),
          ...liveQueryListener('tasks', emit, fail),
        ),
        apply: changes => this.applyTasks(changes, notify && isLiveReady(key)),
        evict: paths => this.evictTasks(paths),
      }
    },

    taskSpec(id: string): LiveSpec<TaskFirestoreData> {
      const path = `tasks/${id}`
      return {
        key: path,
        listen: (emit, fail) => onSnapshot(doc(useFirestore(), 'tasks', id), ...liveDocListener(path, emit, fail)),
        apply: changes => this.applyTasks(changes, false),
        evict: paths => this.evictTasks(paths),
      }
    },

    /**
     * Put tasks into the store. With `notify`, a task new to the store is
     * announced as created (unless it suppresses that), one whose status
     * changed as updated or completed.
     */
    applyTasks(changes: LiveChange<TaskFirestoreData>[], notify: boolean) {
      const notices: TaskNotice[] = []
      this.$patch((state) => {
        for (const { path, data } of changes) {
          const taskId = path.slice('tasks/'.length)
          try {
            const task = TaskFactory.createTaskFromFirestore({ ...data!, id: taskId })
            const previous = state.tasks[path]
            state.tasks[path] = task
            if (!notify) continue
            if (!previous) {
              if (task.metadata?.suppressCreationNotification !== true) notices.push({ taskId, type: 'created' })
            }
            else if (previous.status !== task.status) {
              notices.push({ taskId, type: task.status === 'completed' ? 'completed' : 'updated' })
            }
          }
          catch (error) {
            console.error(`[Tasks] Error processing task ${taskId}:`, error)
          }
        }
      })
      if (notices.length > 0) {
        this.createTaskNotifications(notices).catch((error) => {
          console.error('[Tasks] Error creating notifications:', error)
        })
      }
    },

    evictTasks(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.tasks[path]
      })
    },

    /**
    * Create notifications for task changes
    * @param notifications Array of task notifications to create
    */
    async createTaskNotifications(
      notifications: Array<{ taskId: string, type: 'created' | 'updated' | 'completed' }>,
    ): Promise<void> {
      const user = useCurrentUser().value
      if (!user?.uid) return

      const notificationsStore = useNotificationsStore()

      for (const notification of notifications) {
        try {
          const task = this.tasks[`tasks/${notification.taskId}`]
          if (!task) continue

          // Skip tasks that handle their own notifications via WithNotifications trait
          // Those tasks should be handling their own notifications internally
          if (hasNotifications(task)) {
            continue
          }

          // Determine notification content based on type
          let title = ''
          let body = ''
          const category: NotificationCategory = 'task'

          switch (notification.type) {
            case 'created':
              title = 'New Task'
              body = `A new task has been assigned: ${task.title}`
              break
            case 'updated':
              title = 'Task Updated'
              body = `Task updated: ${task.title} (${task.status})`
              break
            case 'completed':
              title = 'Task Completed'
              body = `Task completed: ${task.title}`
              break
          }

          // Create the notification
          await notificationsStore.createNotification(user.uid, {
            title,
            body,
            category,
            linkType: 'task',
            linkTarget: task.id,
            priority: task.priority === 'critical' ? 'high' : 'normal',
            metadata: {
              taskId: task.id,
              taskType: task.taskType,
              taskStatus: task.status,
            },
          })
        }
        catch (error) {
          console.error('[Tasks] Error creating notification for task:', notification.taskId, error)
        }
      }
    },

    /**
     * Create a SubKrewe join request task
     * @param memberId The member requesting to join
     * @param subkreweId The SubKrewe they want to join
     * @param memberName Optional member name for display
     * @param subkreweName Optional SubKrewe name for display
     * @returns Task ID if successful
     */
    async createSubKreweJoinRequest(
      memberId: string,
      subkreweId: string,
      memberName?: string,
      subkreweName?: string,
    ): Promise<string> {
      // console.warn('Creating SubKrewe join request:', memberId, subkreweId)
      try {
        // Prepare stores up front
        const membersStore = useMembersStore()
        const subkrewesStore = useSubKrewesStore()

        let memberDetails: Member | undefined | null
        let subkreweDetails: SubKrewe | undefined | null

        // Fetch names if not provided
        if (!memberName || !subkreweName) {
          if (!memberName) {
            memberDetails = await membersStore.fetchMember(memberId)
            memberName = memberDetails?.name
          }

          if (!subkreweName) {
            subkreweDetails = await subkrewesStore.fetchSubKrewe(subkreweId)
            subkreweName = subkreweDetails?.name
          }
        }

        // If we still don't have member details, fetch to determine type metadata
        if (!memberDetails) {
          memberDetails = await membersStore.fetchMember(memberId)
        }

        // Create the task through TaskFactory
        const task = TaskFactory.createNewTask('subkrewe_join_request', {
          memberId,
          subkreweId,
          memberName,
          subkreweName,
        }) as SubKreweJoinRequestTask

        // Include member type, email, and phone in metadata for captain notifications
        try {
          const typeStr = String(memberDetails?.member_type || '').toLowerCase()
          const additionalMetadata: Record<string, unknown> = {}
          if (typeStr === 'redshirt') {
            additionalMetadata.memberType = 'redshirt'
          }
          if (memberDetails?.email) {
            additionalMetadata.applicantEmail = memberDetails.email
          }
          else {
            console.warn('createSubKreweJoinRequest: member email unexpectedly missing for', memberId)
          }
          if (memberDetails?.phone) {
            additionalMetadata.applicantPhone = memberDetails.phone
          }
          else {
            console.warn('createSubKreweJoinRequest: member phone unexpectedly missing for', memberId)
          }
          if (Object.keys(additionalMetadata).length > 0) {
            task.metadata = {
              ...task.metadata,
              ...additionalMetadata,
            }
          }
        }
        catch {
          // noop
        }

        // Save to Firestore and state
        const taskId = await this.createTask(task)
        // console.warn(hasNotifications(task))
        // Send notifications about the new application
        if (hasNotifications(task)) {
          try {
            await task.notifySubmitted()
            // console.warn('Join request notification sent successfully')
          }
          catch (error) {
            console.error('Error sending join request notification:', error)
            // Continue even if notification fails - don't rethrow
          }
        }

        return taskId
      }
      catch (error) {
        console.error('Error creating SubKrewe join request:', error)
        throw error
      }
    },

    /**
     * Process a SubKrewe join request (approve or deny)
     * @param taskId The task ID
     * @param approve Whether to approve or deny
     * @param actingMemberId ID of the member taking the action
     * @param reason Optional reason for denial
     * @returns Success status
     */
    async processSubKreweJoinRequest(
      taskId: string,
      approve: boolean,
      actingMemberId?: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        const task = await this.fetchTask(taskId)
        if (!task) {
          throw new Error(`Task not found: ${taskId}`)
        }

        if (task.taskImplementation !== 'SubKreweJoinRequestTask') {
          throw new Error(`Expected SubKreweJoinRequestTask, got ${task.taskImplementation}`)
        }

        // Record who approved/denied the request
        if (actingMemberId) {
          if (approve) {
            task.metadata.approvedById = actingMemberId
          }
          else {
            task.metadata.deniedById = actingMemberId
          }
        }

        // Cast to access the specific methods
        const joinTask = task as unknown as {
          approve(): Promise<boolean>
          deny(reason?: string): Promise<boolean>
        }

        let success: boolean
        if (approve) {
          success = await joinTask.approve()
        }
        else {
          success = await joinTask.deny(reason)
        }

        // Update the task in our store
        if (success) {
          await this.updateTask(task)
        }

        return success
      }
      catch (error) {
        console.error('Error processing SubKrewe join request:', error)
        return false
      }
    },

    /**
     * Create a SubKrewe approval task
     * @param subkreweId The SubKrewe pending approval
     * @param subkreweName Optional name of the SubKrewe for display
     * @param captainId Optional id of the SubKrewe captain
     * @param captainName Optional name of the SubKrewe captain for display
     * @returns Task ID if successful
     */
    async createSubKreweApprovalTask(
      subkreweId: string,
      subkreweName?: string,
      captainId?: string,
      captainName?: string,
    ): Promise<string> {
      try {
        // Check if this SubKrewe is a band to determine task assignment
        const subkrewesStore = useSubKrewesStore()
        const subkrewe = await subkrewesStore.fetchSubKrewe(subkreweId)
        const isBand = subkrewe?.music === 'band' || subkrewe?.music === 'bring_band'

        // Get admin users to assign the task to
        const membersStore = useMembersStore()
        const adminUsers = membersStore.membersArray.filter(member => member.roles?.admin === true)

        // For band SubKrewes, also include band admins
        const bandAdminUsers = isBand
          ? membersStore.membersArray.filter(member => member.roles?.band_admin === true)
          : []

        // Combine admin and band admin users (remove duplicates)
        const allAdminUsers = [...adminUsers, ...bandAdminUsers.filter(bandAdmin =>
          !adminUsers.some(admin => admin.id === bandAdmin.id),
        )]

        if (allAdminUsers.length === 0) {
          throw new Error('No admin users found to assign SubKrewe approval task')
        }

        // Create a new approval task
        const task = new SubKreweApprovalTask(
          subkreweId,
          subkreweName || '',
          captainId || '',
          captainName || '',
        )

        // Set status to pending
        task.status = 'pending'

        // Assign task to the first admin user (primary assignee)
        // All admins will see it via the taskType='admin' subscription
        const primaryAdmin = allAdminUsers[0]
        if (!primaryAdmin?.id) {
          throw new Error('Primary admin user has invalid ID')
        }
        task.assign(primaryAdmin.id, 'member')

        // Add band info to metadata for proper task routing
        if (task.metadata && typeof task.metadata === 'object') {
          task.metadata.isBand = isBand
          // Store all admin user IDs who should see this task
          task.metadata.assignedAdminIds = allAdminUsers.map(admin => admin.id)
        }

        // Create the task in the database
        const taskId = await this.createTask(task)

        // Now handle notification - make sure the task has its ID set
        if (taskId) {
          task.id = taskId
          // Call notifySubmitted directly - this will notify admins (and band admins for bands)
          if (hasNotifications(task)) {
            await task.notifySubmitted()
          }
        }

        return taskId
      }
      catch (error) {
        console.error('Error creating SubKrewe approval task:', error)
        throw error
      }
    },

    /**
     * Process a SubKrewe approval task (approve or deny)
     * @param taskId The task ID
     * @param approve Whether to approve or deny
     * @param actingMemberId ID of the admin taking the action
     * @param reason Optional reason for denial
     * @returns Success status
     */
    async processSubKreweApprovalTask(
      taskId: string,
      approve: boolean,
      actingMemberId?: string,
      reason?: string,
    ): Promise<boolean> {
      try {
        // Fetch the task
        const task = await this.fetchTask(taskId) as SubKreweApprovalTask
        if (!task || task.taskImplementation !== 'SubKreweApprovalTask') {
          throw new Error('Not a valid SubKrewe approval task')
        }

        // Record who took the action if available
        if (actingMemberId) {
          if (approve) {
            task.metadata.approvedById = actingMemberId
          }
          else {
            task.metadata.deniedById = actingMemberId
          }
        }

        // Execute the appropriate action
        let success = false
        if (approve) {
          success = await task.approve()
        }
        else {
          success = await task.deny(reason)
        }

        // If the action succeeded, update the task in the database
        if (success) {
          await this.updateTask(task)
        }

        return success
      }
      catch (error) {
        console.error('Error processing SubKrewe approval task:', error)
        return false
      }
    },

    /**
     * Clear all active subscriptions
     */
    clearSubscriptions(): void {
      this.activeSubscriptions.forEach(unsubscribe => unsubscribe())
      this.activeSubscriptions = []
    },

    /**
     * Reset the store state
     */
    resetState(): void {
      this.clearSubscriptions()
      // Important: replace tasks object to truly clear keys (deep-merge with {} would keep existing keys)
      this.tasks = {}
      this.loading = false
      this.error = null
      this.initialized = false
    },

  },
})
